// Making a creator's brand colour survive dark mode.
//
// THE PROBLEM
// brand_settings.theme_color1 is picked against a LIGHT canvas — the admin's
// Brand preview, the login page, the landing pages. Several shipped presets are
// deliberately dark ("Notion Ink" is #2f3437, near-black ink on cream) and
// creators pick dark custom colours for the same reason. Flip the member app to
// dark mode and that colour lands on #14171C at a contrast ratio of 1.4, i.e.
// invisible: headings, links, active nav items, progress bars and solid buttons
// all disappear into the background.
//
// THE FIX, AND ITS LIMITS
// Don't ask the creator to maintain a second palette. Keep their hue and
// saturation and lift only the LIGHTNESS — but ONLY for a colour that actually
// fails, and the bar for "fails" is deliberately low (RESCUE_BELOW, the WCAG
// minimum for a UI component). A palette that already reads in dark mode is
// passed through byte-for-byte, so everything that was QA-ed in dark mode looks
// exactly as it did. Of the ten shipped presets only Notion Ink and Royal move.
//
// Deciding to rescue at 3.0 but lifting all the way to RESCUE_TO is on purpose:
// a colour that was invisible should come back with real headroom, not sitting
// a hundredth over the line where the next tweak buries it again.

/** The surface most brand-coloured UI sits on in dark mode (--c-surface). */
export const DARK_SURFACE = { r: 20, g: 23, b: 28 };

/** …and in light mode. */
export const LIGHT_SURFACE = { r: 255, g: 255, b: 255 };

/* ── One accent for both modes ──────────────────────────────────────────────
 * Rescuing only the dark side leaves the two themes looking like different
 * brands: the stock palette renders near-black accents in light mode and cyan
 * ones in dark, because #012329 is a 189° cyan sitting at 8% lightness — it
 * READS as black, but it is not black, and lifting it for dark mode is what
 * finally reveals the hue.
 *
 * So when a colour is a real colour that merely renders as black, both modes
 * get the SAME lifted accent. The window is narrow — a colour has to clear the
 * dark surface AND white at once — but it exists (for that cyan, #047d93 is
 * 3.74:1 on #14171C and 4.80:1 on white).
 *
 * The two gates are what keep this from flattening deliberate palettes:
 *   · CHROMATIC_MIN — "Notion Ink" is a 8%-saturation near-black. Ink IS the
 *     brand; turning its light mode grey would be vandalism, not a fix. It
 *     stays ink in light and gets the dark-only lift.
 *   · BLACKISH_MAX — Royal (#6d28d9) is already a visible violet at 50%
 *     lightness. Its light mode was never broken, so it is left alone.
 */
const CHROMATIC_MIN = 0.25;
const BLACKISH_MAX = 0.2;
const SHARED_ON_DARK = 3.5;
const SHARED_ON_LIGHT = 4.5;

/** Below this against the dark surface, the colour is not usable. */
const RESCUE_BELOW = 3.0;

/** Where a rescued colour lands. */
const RESCUE_TO = 4.0;

/** A pure-white accent looks broken rather than branded, so stop short. */
const MAX_LIGHTNESS = 0.9;

export function parseHex(input) {
  let h = String(input || "").trim().replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

export function toHex({ r, g, b }) {
  const c = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** WCAG relative luminance. */
export function luminance({ r, g, b }) {
  const ch = (v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}

/** WCAG contrast ratio between two rgb triples (1 … 21). */
export function contrast(a, b) {
  const l1 = luminance(a);
  const l2 = luminance(b);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

/** Contrast of a hex against the dark surface; 21 for anything unparseable. */
export function contrastOnDark(color) {
  const rgb = parseHex(color);
  return rgb ? contrast(rgb, DARK_SURFACE) : 21;
}

/* ── HSL, so a lift changes brightness without shifting the hue ─────────── */

export function rgbToHsl({ r, g, b }) {
  const rr = r / 255, gg = g / 255, bb = b / 255;
  const max = Math.max(rr, gg, bb);
  const min = Math.min(rr, gg, bb);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === rr) h = ((gg - bb) / d + (gg < bb ? 6 : 0)) / 6;
  else if (max === gg) h = ((bb - rr) / d + 2) / 6;
  else h = ((rr - gg) / d + 4) / 6;
  return { h, s, l };
}

export function hslToRgb({ h, s, l }) {
  if (s === 0) {
    const v = l * 255;
    return { r: v, g: v, b: v };
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = (t) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  return { r: hue(h + 1 / 3) * 255, g: hue(h) * 255, b: hue(h - 1 / 3) * 255 };
}

/** Raise a colour's lightness until it clears `target`, keeping hue + saturation. */
function lift(hsl, target) {
  for (let l = Math.max(hsl.l, 0.2); l <= MAX_LIGHTNESS; l += 0.005) {
    const candidate = hslToRgb({ h: hsl.h, s: hsl.s, l });
    if (contrast(candidate, DARK_SURFACE) >= target) return { rgb: candidate, l };
  }
  return { rgb: hslToRgb({ h: hsl.h, s: hsl.s, l: MAX_LIGHTNESS }), l: MAX_LIGHTNESS };
}

/**
 * The creator's colour, made readable on the dark surface.
 *
 * Returns { color, delta } — `delta` is the lightness the colour had to gain, 0
 * when nothing was touched. Unparseable input (a gradient, `rgb()`, a keyword,
 * empty) comes back untouched, so this is safe to run over every brand value.
 */
export function rescueForDark(color) {
  const rgb = parseHex(color);
  if (!rgb) return { color, delta: 0 };
  if (contrast(rgb, DARK_SURFACE) >= RESCUE_BELOW) return { color, delta: 0 };

  const hsl = rgbToHsl(rgb);
  const { rgb: out, l } = lift(hsl, RESCUE_TO);
  return { color: toHex(out), delta: l - hsl.l };
}

/** Apply a known lightness gain to a colour — used to keep a gradient's two
 *  stops in the same relationship after the primary has been rescued. */
export function shiftLightness(color, delta) {
  const rgb = parseHex(color);
  if (!rgb || delta <= 0) return color;
  const hsl = rgbToHsl(rgb);
  return toHex(hslToRgb({ h: hsl.h, s: hsl.s, l: Math.min(MAX_LIGHTNESS, hsl.l + delta) }));
}

/**
 * The bar a button label has to clear before we overrule the creator.
 *
 * Deliberately far below WCAG AA. White on a mid-tone brand colour is around
 * 2.2:1 (white on the FlexiFunnels green #22c55e is exactly 2.24) and is what
 * every brand on earth ships — overruling that would be us redesigning their
 * button, not fixing it. Anything under 1.8 is not a design choice, it is a
 * label you cannot see: black on the stock #012329 is 1.27.
 */
const LABEL_FLOOR = 1.8;

/**
 * A readable label colour for a solid button in `bg`.
 *
 * The creator's own choice is kept unless it is genuinely unreadable — see
 * LABEL_FLOOR. Two ways to get there: the dark-mode rescue moved the button's
 * background out from under a label chosen for a much darker one, or the stored
 * palette never worked in the first place (the stock default pairs a near-black
 * #012329 button with #000000 text, which is why "Start course" and "Go to
 * community" render as a black-on-black slab in LIGHT mode too).
 */
export function readableOn(bg, preferred = "#ffffff") {
  const bgRgb = parseHex(bg);
  if (!bgRgb) return preferred;
  const pref = parseHex(preferred);
  if (pref && contrast(pref, bgRgb) >= LABEL_FLOOR) return preferred;
  const white = { r: 255, g: 255, b: 255 };
  const ink = { r: 17, g: 19, b: 23 };
  return contrast(white, bgRgb) >= contrast(ink, bgRgb) ? "#ffffff" : "#111317";
}

/**
 * One accent usable on BOTH canvases, or null when this colour should keep a
 * per-mode treatment.
 *
 * Null is the common answer — it means either the colour already works (no
 * rescue needed at all), or it is a deliberate ink/near-neutral, or it is
 * already light enough to read in light mode.
 */
export function unifiedAccent(color) {
  const rgb = parseHex(color);
  if (!rgb) return null;

  const { h, s, l } = rgbToHsl(rgb);
  // A real colour (not an ink) that is dark enough to render as black.
  if (s < CHROMATIC_MIN || l > BLACKISH_MAX) return null;

  let lo = null;
  let hi = null;
  for (let cand = 0.1; cand <= 0.7; cand += 0.005) {
    const c = hslToRgb({ h, s, l: cand });
    if (contrast(c, DARK_SURFACE) >= SHARED_ON_DARK && contrast(c, LIGHT_SURFACE) >= SHARED_ON_LIGHT) {
      if (lo === null) lo = cand;
      hi = cand;
    }
  }
  if (lo === null) return null;

  // Middle of the window: the two bounds pull in opposite directions, so the
  // midpoint is the value furthest from failing either canvas.
  return toHex(hslToRgb({ h, s, l: (lo + hi) / 2 }));
}

/**
 * The whole palette for a mode. Light mode is the creator's palette verbatim —
 * they chose it against a light canvas and it is already correct there.
 */
export function paletteForMode(brand, mode) {
  const b = brand || {};
  const primary = b.theme_color1 || "#0274F7";
  const secondary = b.theme_color2 || null;
  const btnText = b.theme_button_txt_color || "#ffffff";

  // A colour that only LOOKS black gets one accent for both modes, so the two
  // themes are recognisably the same brand rather than "black app" and "cyan
  // app". Null for everything else, which keeps light mode verbatim.
  const shared = unifiedAccent(primary);

  if (mode !== "dark") {
    // Light mode is the creator's palette as they chose it. Two exceptions, and
    // both are repairs rather than restyling: the shared accent above, and the
    // button label, because a label you cannot read is broken in every mode —
    // the stock palette ships #000000 on #012329, at 1.27:1.
    const lightPrimary = shared || primary;
    return {
      primary: lightPrimary,
      secondary,
      btnText: readableOn(lightPrimary, btnText),
      rescued: Boolean(shared),
    };
  }

  const { color: lifted, delta } = rescueForDark(primary);
  const darkPrimary = shared || lifted;
  // Secondary is judged on its own. Carrying the primary's lift across to it
  // would wash out a stop that was already bright — the stock palette pairs a
  // near-black #012329 with a vivid #03B0D0, and only the first one is broken.
  const nextSecondary = secondary ? rescueForDark(secondary).color : secondary;

  return {
    primary: darkPrimary,
    secondary: nextSecondary,
    btnText: readableOn(darkPrimary, btnText),
    rescued: Boolean(shared) || delta > 0 || nextSecondary !== secondary,
  };
}
